import { useEffect, useRef, useState } from 'react';
import { Button, CircularProgress } from '@mui/material';
import { FaPlus, FaSearch, FaTimes } from 'react-icons/fa';
import trashIcon from '../../../../assets/trash.png';
import pencilIcon from '../../../../assets/pencil.png';
import infoIcon from '../../../../assets/info.png';
import alertCircleIcon from '../../../../assets/alert-circle.png';
import LoadingSubmitButton from '../../../../components/LoadingSubmitButton';

const BLACKLIST_LABELS = {
  'trimester 1': 'אסור בטרימסטר ראשון',
  'trimester 2': 'אסור בטרימסטר שני',
  'trimester 3': 'אסור בטרימסטר שלישי',
  'nursing': 'אסור בהנקה',
};

const BLACKLIST_KEYS = ['trimester 1', 'trimester 2', 'trimester 3', 'nursing'];

const EMPTY_CCT_RANGE = {
  predicate: {
    minCCT: '',
    maxCCT: '',
  },
  details: {
    overrideMode: '',
  },
};

const EMPTY_RENAL_ADJUSTMENT_ADULT = {
  hemodialysis: '',
  capd: '',
  crrt: '',
  cct: [],
};

const CCT_OVERLAP_ERROR = 'יש להגדיר טווחים שאינם חופפים';

const EditIcon = () => <img src={pencilIcon} alt="ערוך" style={{ width: 24, height: 24, objectFit: 'contain' }} />;

const DeleteIcon = () => <img src={trashIcon} alt="מחק" style={{ width: 24, height: 24, objectFit: 'contain' }} />;

const DrugsTab = ({
  drugs,
  editingDrug,
  newDrug,
  setNewDrug,
  handleAddDrug,
  handleEditDrug,
  handleSaveDrug,
  handleDeleteDrug,
  setEditingDrug,
  blacklists,
  onPreviewChange,
  saveRef,
  dirtyRef,
}) => {
  const [isAdding, setIsAdding] = useState(false);
  const [editedDrug, setEditedDrugRaw] = useState(null);
  const setEditedDrug = (val) => {
    if (dirtyRef) dirtyRef.current = true;
    setEditedDrugRaw(val);
  };
  const [editedBlacklists, setEditedBlacklists] = useState({});
  const [newDrugBlacklists, setNewDrugBlacklists] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [deletingIndex, setDeletingIndex] = useState(null);
  const [newCctError, setNewCctError] = useState('');
  const [editedCctError, setEditedCctError] = useState('');
  const latestNewCct = useRef(null);
  const latestEditedCct = useRef(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    return () => {
      onPreviewChange?.(null);
    };
  }, [onPreviewChange]);

  useEffect(() => {
    if (saveRef) {
      if (editingDrug !== null && editedDrug) {
        saveRef.current = saveEditing;
      } else if (isAdding) {
        saveRef.current = confirmAdd;
      } else {
        saveRef.current = null;
      }
    }
  });

  useEffect(() => {
    if (!onPreviewChange) return;

    if (editingDrug === null || editingDrug === undefined || !editedDrug) {
      onPreviewChange(null);
      return;
    }

    const originalDrug = drugs[editingDrug];
    const drugId = editedDrug.id || originalDrug?.id;
    if (!drugId) {
      onPreviewChange(null);
      return;
    }

    const previewDrugs = drugs.map((drug, index) =>
      index === editingDrug ? { ...editedDrug, id: drugId } : drug
    );

    const previewBlacklists = { ...blacklists };
    BLACKLIST_KEYS.forEach((key) => {
      const values = new Set(blacklists[key] || []);
      if (editedBlacklists[key]) {
        values.add(drugId);
      } else {
        values.delete(drugId);
      }
      previewBlacklists[key] = Array.from(values);
    });

    onPreviewChange({
      drugs: previewDrugs,
      blacklists: previewBlacklists,
    });
  }, [blacklists, drugs, editedBlacklists, editedDrug, editingDrug, onPreviewChange]);

  const normalizeCctRanges = (ranges = []) =>
    ranges.map((range) => ({
      predicate: {
        minCCT: range?.predicate?.minCCT ?? '',
        maxCCT: range?.predicate?.maxCCT ?? '',
      },
      details: {
        overrideMode: range?.details?.overrideMode ?? '',
      },
    }));

  const getOverlappingCctIndices = (ranges = []) => {
    const mapped = ranges.map((range, originalIndex) => {
      const rawMin = range?.predicate?.minCCT;
      const rawMax = range?.predicate?.maxCCT;
      const min = rawMin === '' || rawMin === null || rawMin === undefined ? Number.NEGATIVE_INFINITY : Number(rawMin);
      const max = rawMax === '' || rawMax === null || rawMax === undefined ? Number.POSITIVE_INFINITY : Number(rawMax);
      return { min, max, originalIndex, rawMin, rawMax };
    }).filter(r => !Number.isNaN(r.min) && !Number.isNaN(r.max) && r.min <= r.max)
      .sort((a, b) => a.min - b.min);

    // Map: later-added originalIndex -> earlier conflicting range info
    // "later-added" = higher originalIndex (the one the user added second)
    const conflicting = new Map();
    for (let i = 1; i < mapped.length; i++) {
      if (mapped[i].min < mapped[i - 1].max) {
        const laterIdx = mapped[i].originalIndex > mapped[i - 1].originalIndex ? mapped[i] : mapped[i - 1];
        const earlierIdx = laterIdx === mapped[i] ? mapped[i - 1] : mapped[i];
        conflicting.set(laterIdx.originalIndex, earlierIdx);
      }
    }
    return conflicting;
  };

  const hasOverlappingCctRanges = (ranges = []) => {
    const validRanges = ranges
      .map((range) => {
        const rawMin = range?.predicate?.minCCT;
        const rawMax = range?.predicate?.maxCCT;

        const min =
          rawMin === '' || rawMin === null || rawMin === undefined
            ? Number.NEGATIVE_INFINITY
            : Number(rawMin);

        const max =
          rawMax === '' || rawMax === null || rawMax === undefined
            ? Number.POSITIVE_INFINITY
            : Number(rawMax);

        return { min, max };
      })
      .filter((range) => !Number.isNaN(range.min) && !Number.isNaN(range.max))
      .filter((range) => range.min <= range.max)
      .sort((a, b) => a.min - b.min);

    for (let i = 1; i < validRanges.length; i += 1) {
      if (validRanges[i].min < validRanges[i - 1].max) {
        return true;
      }
    }

    return false;
  };

  const buildCctDetails = (field, value, currentDetails = {}) => {
    if (field === 'overrideMode') {
      return {
        overrideMode: value,
      };
    }

    return {
      overrideMode: currentDetails?.overrideMode ?? '',
    };
  };

  const updateNewDrugCct = (index, field, value) => {
    const currentCct = normalizeCctRanges(newDrug.renalAdjustmentAdult?.cct);

    const updatedCct = currentCct.map((item, i) => {
      if (i !== index) return item;

      if (field === 'minCCT' || field === 'maxCCT') {
        return {
          ...item,
          predicate: {
            ...item.predicate,
            [field]: value,
          },
        };
      }

      return {
        ...item,
        details: buildCctDetails(field, value, item.details),
      };
    });

    latestNewCct.current = updatedCct;
    setNewDrug({
      ...newDrug,
      renalAdjustmentAdult: {
        ...EMPTY_RENAL_ADJUSTMENT_ADULT,
        ...(newDrug.renalAdjustmentAdult || {}),
        cct: updatedCct,
      },
    });
  };

  const validateNewCct = () => {
    setNewCctError(hasOverlappingCctRanges(latestNewCct.current ?? newDrug.renalAdjustmentAdult?.cct) ? CCT_OVERLAP_ERROR : '');
  };

  const addNewDrugCctRange = () => {
    const currentCct = normalizeCctRanges(newDrug.renalAdjustmentAdult?.cct);

    setNewDrug({
      ...newDrug,
      renalAdjustmentAdult: {
        ...EMPTY_RENAL_ADJUSTMENT_ADULT,
        ...(newDrug.renalAdjustmentAdult || {}),
        cct: [...currentCct, { ...EMPTY_CCT_RANGE }],
      },
    });
  };

  const removeNewDrugCctRange = (index) => {
    const currentCct = normalizeCctRanges(newDrug.renalAdjustmentAdult?.cct);
    const updatedCct = currentCct.filter((_, i) => i !== index);

    setNewDrug({
      ...newDrug,
      renalAdjustmentAdult: {
        ...EMPTY_RENAL_ADJUSTMENT_ADULT,
        ...(newDrug.renalAdjustmentAdult || {}),
        cct: updatedCct,
      },
    });

    setNewCctError(hasOverlappingCctRanges(updatedCct) ? CCT_OVERLAP_ERROR : '');
  };

  const updateEditedDrugCct = (index, field, value) => {
    const currentCct = normalizeCctRanges(editedDrug?.renalAdjustmentAdult?.cct);

    const updatedCct = currentCct.map((item, i) => {
      if (i !== index) return item;

      if (field === 'minCCT' || field === 'maxCCT') {
        return {
          ...item,
          predicate: {
            ...item.predicate,
            [field]: value,
          },
        };
      }

      return {
        ...item,
        details: buildCctDetails(field, value, item.details),
      };
    });

    latestEditedCct.current = updatedCct;
    setEditedDrug((prev) => ({
      ...prev,
      renalAdjustmentAdult: {
        ...EMPTY_RENAL_ADJUSTMENT_ADULT,
        ...(prev?.renalAdjustmentAdult || {}),
        cct: updatedCct,
      },
    }));
  };

  const validateEditedCct = () => {
    setEditedCctError(hasOverlappingCctRanges(latestEditedCct.current ?? editedDrug?.renalAdjustmentAdult?.cct) ? CCT_OVERLAP_ERROR : '');
  };

  const addEditedDrugCctRange = () => {
    const currentCct = normalizeCctRanges(editedDrug?.renalAdjustmentAdult?.cct);

    setEditedDrug((prev) => ({
      ...prev,
      renalAdjustmentAdult: {
        ...EMPTY_RENAL_ADJUSTMENT_ADULT,
        ...(prev?.renalAdjustmentAdult || {}),
        cct: [...currentCct, { ...EMPTY_CCT_RANGE }],
      },
    }));
  };

  const removeEditedDrugCctRange = (index) => {
    const currentCct = normalizeCctRanges(editedDrug?.renalAdjustmentAdult?.cct);
    const updatedCct = currentCct.filter((_, i) => i !== index);

    setEditedDrug((prev) => ({
      ...prev,
      renalAdjustmentAdult: {
        ...EMPTY_RENAL_ADJUSTMENT_ADULT,
        ...(prev?.renalAdjustmentAdult || {}),
        cct: updatedCct,
      },
    }));

    setEditedCctError(hasOverlappingCctRanges(updatedCct) ? CCT_OVERLAP_ERROR : '');
  };

  const startEditing = (index) => {
    if (dirtyRef) dirtyRef.current = false;
    const drug = drugs[index];
    const normalizedCct = normalizeCctRanges(drug.renalAdjustmentAdult?.cct);

    setEditedDrugRaw({
      ...drug,
      renalAdjustmentAdult: {
        ...EMPTY_RENAL_ADJUSTMENT_ADULT,
        ...(drug.renalAdjustmentAdult || {}),
        cct: normalizedCct,
      },
    });

    const bl = {};
    BLACKLIST_KEYS.forEach((key) => {
      bl[key] = (blacklists[key] || []).includes(drug.id);
    });

    setEditedBlacklists(bl);
    setEditedCctError('');
    handleEditDrug(index);
    setIsAdding(false);
  };

  const cancelEditing = () => {
    if (dirtyRef) dirtyRef.current = false;
    setEditedDrugRaw(null);
    setEditingDrug(null);
    setIsAdding(false);
    setEditedCctError('');
  };

  const saveEditing = async () => {
    if (!editedDrug) return;

    const hasOverlap = hasOverlappingCctRanges(editedDrug.renalAdjustmentAdult?.cct);
    if (hasOverlap) {
      setEditedCctError(CCT_OVERLAP_ERROR);
      return;
    }

    const drug = drugs[editingDrug];

    const oldBlacklists = {};
    BLACKLIST_KEYS.forEach((key) => {
      oldBlacklists[key] = (blacklists[key] || []).includes(drug.id);
    });

    setIsSaving(true);
    const success = await handleSaveDrug(
      editingDrug,
      editedDrug,
      editedBlacklists,
      undefined,
      oldBlacklists
    );
    setIsSaving(false);

    if (success) {
      setEditedDrug(null);
      setEditedCctError('');
    }
  };

  const startAdding = () => {
    setIsAdding(true);
    setEditingDrug(null);
    setEditedDrug(null);
    setNewDrugBlacklists({});
    setNewCctError('');

    setNewDrug({
      genericName: '',
      displayName: '',
      renalAdjustmentAdult: { ...EMPTY_RENAL_ADJUSTMENT_ADULT },
    });
  };

  const confirmAdd = async () => {
    const hasOverlap = hasOverlappingCctRanges(newDrug.renalAdjustmentAdult?.cct);
    if (hasOverlap) {
      setNewCctError(CCT_OVERLAP_ERROR);
      return;
    }

    setIsSaving(true);
    const success = await handleAddDrug({ blacklists: newDrugBlacklists });
    setIsSaving(false);

    if (success) {
      setIsAdding(false);
      setNewDrugBlacklists({});
      setNewCctError('');
      setNewDrug({
        genericName: '',
        displayName: '',
        renalAdjustmentAdult: { ...EMPTY_RENAL_ADJUSTMENT_ADULT },
      });
    }
  };

  const handleDeleteClick = async (index) => {
    setDeletingIndex(index);
    await handleDeleteDrug(index);
    setDeletingIndex(null);
  };

  const drugBlacklistLabels = (drugId) =>
    BLACKLIST_KEYS.filter((key) => (blacklists[key] || []).includes(drugId)).map(
      (key) => BLACKLIST_LABELS[key]
    );

  const renderCctRanges = ({ ranges, onChange, onRemove, error, onAdd, onBlur, idPrefix = 'cct-range' }) => {
    const overlappingMap = error ? getOverlappingCctIndices(normalizeCctRanges(ranges)) : new Map();
    return (
    <>
      <div
        style={{
          marginTop: '40px',
          marginBottom: '12px',
          color: '#103C6E',
          fontSize: '16px',
          fontWeight: 500,
          lineHeight: '1.5',
        }}
        dir="rtl"
      >
        טווחי CCT
      </div>

      {normalizeCctRanges(ranges).map((range, index) => (
        <div key={index} id={`${idPrefix}-${index}`} style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '10px', direction: 'ltr' }}>
          <button
            onClick={() => onRemove(index)}
            className="action-icon delete-icon"
            style={{ flexShrink: 0, alignSelf: 'center' }}
          >
            <img src={trashIcon} alt="הסר טווח" style={{ width: 24, height: 24, objectFit: 'contain' }} />
          </button>
          <div
            style={{
              flex: 1,
              border: '1px solid #D6E2F0',
              borderRadius: '8px',
              padding: '12px',
              backgroundColor: '#fff',
              direction: 'rtl',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                marginBottom: '12px',
                flexWrap: 'nowrap',
              }}
            >
              <span className="formula-edit-label" style={{ whiteSpace: 'nowrap', minWidth: 'unset' }}>כאשר ה-CCT הוא מינימום</span>
              <input
                className="formula-edit-input"
                type="number"
                value={range.predicate?.minCCT ?? ''}
                onChange={(e) => onChange(index, 'minCCT', e.target.value)}
                onBlur={onBlur}
                placeholder="e.g. 10"
                dir="ltr"
                style={{ flex: 1, padding: '4px 6px', fontSize: '14px' }}
              />
              <span className="formula-edit-label" style={{ whiteSpace: 'nowrap', minWidth: 'unset' }}>ומקסימום</span>
              <input
                className="formula-edit-input"
                type="number"
                value={range.predicate?.maxCCT ?? ''}
                onChange={(e) => onChange(index, 'maxCCT', e.target.value)}
                onBlur={onBlur}
                placeholder="e.g. 50"
                dir="ltr"
                style={{ flex: 1, padding: '4px 6px', fontSize: '14px' }}
              />
            </div>

            {overlappingMap.has(index) && (() => {
              const earlier = overlappingMap.get(index);
              const minVal = earlier.rawMin === '' || earlier.rawMin === null || earlier.rawMin === undefined ? '∞-' : earlier.rawMin;
              const maxVal = earlier.rawMax === '' || earlier.rawMax === null || earlier.rawMax === undefined ? '∞' : earlier.rawMax;
              const rangeStr = `${minVal}–${maxVal}`;
              return (
                <div
                  dir="rtl"
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '8px',
                    backgroundColor: 'rgba(255, 45, 85, 0.08)',
                    border: '1px solid rgba(255, 45, 85, 0.3)',
                    borderRadius: '8px',
                    padding: '10px 14px',
                    marginBottom: '12px',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#103C6E',
                  }}
                >
                  <img src={alertCircleIcon} alt="" style={{ width: 18, height: 18, objectFit: 'contain', flexShrink: 0, marginTop: '2px' }} />
                  <span style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <span>{`טווח ה-CCT שהוגדר חופף לטווח קיים (${rangeStr}).`}</span>
                    <span>יש להגדיר טווחים שאינם חופפים.</span>
                    <a
                      href={`#${idPrefix}-${earlier.originalIndex}`}
                      onClick={(e) => {
                        e.preventDefault();
                        document.getElementById(`${idPrefix}-${earlier.originalIndex}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      }}
                      style={{ color: '#0CAA98', textDecoration: 'underline', cursor: 'pointer' }}
                    >
                      {`לחץ כאן לעריכת הטווח הקיים (${rangeStr}).`}
                    </a>
                  </span>
                </div>
              );
            })()}

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span className="formula-edit-label" style={{ whiteSpace: 'nowrap', minWidth: 'unset' }}>המינון יהיה</span>
              <input
                className="formula-edit-input"
                value={range.details?.overrideMode ?? ''}
                onChange={(e) => onChange(index, 'overrideMode', e.target.value)}
                placeholder="e.g. 500 mg q24h"
                dir="ltr"
                style={{ flex: 1 }}
              />
            </div>
          </div>
        </div>
      ))}

      <div style={{ display: 'flex', justifyContent: 'flex-start', direction: 'rtl' }}>
        <Button
          variant="outlined"
          onClick={onAdd}
          sx={{
            borderColor: '#103C6E',
            color: '#103C6E',
            borderRadius: '8px',
            marginBottom: '16px',
            direction: 'rtl',
          }}
        >
          הוסף טווח CCT
        </Button>
      </div>
    </>
    );
  };

  return (
    <section className="antibiogram-section">
      <div className="tab-description">
        ניהול רשימת התרופות. לכל תרופה ניתן להגדיר אם היא אסורה בהריון (לפי שליש) או בהנקה.
      </div>

      <div className="drug-search-wrapper">
        <FaSearch className="drug-search-icon" size={16} />
        <input
          className="drug-search-input"
          type="text"
          placeholder="חיפוש תרופה"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          dir="rtl"
        />
        <button className="drug-search-clear" onClick={() => setSearchQuery('')} aria-label="נקה חיפוש">
          <FaTimes size={14} />
        </button>
      </div>

      {isAdding ? (
        <div className="formula-item editing" style={{ marginBottom: '24px' }}>
          <div className="formula-edit-form">
            <div className="formula-edit-row" style={{ marginTop: '20px' }}>
              <span className="formula-edit-label">שם גנרי</span>
              <input
                className="formula-edit-input"
                value={newDrug.genericName || ''}
                onChange={(e) =>
                  setNewDrug({
                    ...newDrug,
                    genericName: e.target.value,
                    displayName: e.target.value,
                  })
                }
                placeholder="שם גנרי"
                dir="rtl"
                autoFocus
              />
            </div>

            <div className="formula-edit-row drug-properties-row">
              <span className="formula-edit-label">מאפיינים</span>
              <div className="drug-checkboxes">
                {BLACKLIST_KEYS.map((key) => (
                  <label key={key} className="drug-checkbox-label">
                    <input
                      type="checkbox"
                      checked={newDrugBlacklists[key] || false}
                      onChange={(e) =>
                        setNewDrugBlacklists((prev) => ({
                          ...prev,
                          [key]: e.target.checked,
                        }))
                      }
                    />
                    {BLACKLIST_LABELS[key]}
                  </label>
                ))}
              </div>
            </div>

            <div
              style={{
                marginTop: '40px',
                marginBottom: '12px',
                color: '#103C6E',
                fontSize: '16px',
                fontWeight: 500,
                lineHeight: '1.5',
              }}
              dir="rtl"
            >
              התאמת מינון לפי תפקוד כלייתי
            </div>

            <div className="formula-edit-row">
              <span className="formula-edit-label">
                המודיאליזה
              </span>
              <input
                className="formula-edit-input"
                value={newDrug.renalAdjustmentAdult?.hemodialysis || ''}
                onChange={(e) =>
                  setNewDrug({
                    ...newDrug,
                    renalAdjustmentAdult: {
                      ...EMPTY_RENAL_ADJUSTMENT_ADULT,
                      ...(newDrug.renalAdjustmentAdult || {}),
                      hemodialysis: e.target.value,
                    },
                  })
                }
                placeholder="e.g. 500 mg q24h"
                dir="ltr"
              />
            </div>

            <div className="formula-edit-row">
              <span className="formula-edit-label">CAPD</span>
              <input
                className="formula-edit-input"
                value={newDrug.renalAdjustmentAdult?.capd || ''}
                onChange={(e) =>
                  setNewDrug({
                    ...newDrug,
                    renalAdjustmentAdult: {
                      ...EMPTY_RENAL_ADJUSTMENT_ADULT,
                      ...(newDrug.renalAdjustmentAdult || {}),
                      capd: e.target.value,
                    },
                  })
                }
                placeholder="e.g. 500 mg q24h"
                dir="ltr"
              />
            </div>

            <div className="formula-edit-row">
              <span className="formula-edit-label">CRRT</span>
              <input
                className="formula-edit-input"
                value={newDrug.renalAdjustmentAdult?.crrt || ''}
                onChange={(e) =>
                  setNewDrug({
                    ...newDrug,
                    renalAdjustmentAdult: {
                      ...EMPTY_RENAL_ADJUSTMENT_ADULT,
                      ...(newDrug.renalAdjustmentAdult || {}),
                      crrt: e.target.value,
                    },
                  })
                }
                placeholder="e.g. 500 mg q24h"
                dir="ltr"
              />
            </div>

            {renderCctRanges({
              ranges: newDrug.renalAdjustmentAdult?.cct,
              onChange: updateNewDrugCct,
              onRemove: removeNewDrugCctRange,
              error: newCctError,
              onAdd: addNewDrugCctRange,
              onBlur: validateNewCct,
              idPrefix: 'new-cct-range',
            })}

            <div className="formula-edit-actions">
              <LoadingSubmitButton
                onClick={confirmAdd}
                isLoading={isSaving}
                disabled={!newDrug.genericName}
                text="הוספה"
                loadingText="מוסיף..."
                style={{
                  backgroundColor: '#103C6E',
                  borderRadius: '8px',
                  padding: '8px 30px',
                  border: 'none',
                  color: '#fff',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  fontWeight: 'bold',
                }}
              />
              <Button
                variant="outlined"
                onClick={() => {
                  setIsAdding(false);
                  setNewDrug({
                    genericName: '',
                    displayName: '',
                    renalAdjustmentAdult: { ...EMPTY_RENAL_ADJUSTMENT_ADULT },
                  });
                  setNewDrugBlacklists({});
                  setNewCctError('');
                }}
                disabled={isSaving}
                sx={{
                  borderColor: '#103C6E',
                  color: '#103C6E',
                  borderRadius: '8px',
                  padding: '8px 30px',
                  marginRight: '12px',
                }}
              >
                בטל
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <button className="add-drug-fab" onClick={startAdding} aria-label="הוסף תרופה">
          <FaPlus size={20} />
        </button>
      )}

      <div className="formula-items-list">
        {drugs.filter((drug) => {
          if (!searchQuery) return true;
          const q = searchQuery.toLowerCase();
          return (
            drug.displayName?.toLowerCase().includes(q) ||
            drug.genericName?.toLowerCase().includes(q)
          );
        }).map((drug, _, filteredArr) => {
          const index = drugs.indexOf(drug);
          const isEditing = editingDrug === index;
          const blacklistLabels = drugBlacklistLabels(drug.id);

          return (
            <div key={drug.id || index} className={`formula-item${isEditing ? ' editing' : ''}`}>
              {isEditing ? (
                <div className="formula-edit-form">
                  <div className="formula-edit-row" style={{ marginTop: '20px' }}>
                    <span className="formula-edit-label">שם גנרי</span>
                    <input
                      className="formula-edit-input"
                      value={editedDrug?.genericName || ''}
                      onChange={(e) =>
                        setEditedDrug((prev) => ({
                          ...prev,
                          genericName: e.target.value,
                          displayName: e.target.value,
                        }))
                      }
                      placeholder="שם גנרי"
                      dir="rtl"
                      autoFocus
                    />
                  </div>

                  <div className="formula-edit-row drug-properties-row">
                    <span className="formula-edit-label">מאפיינים</span>
                    <div className="drug-checkboxes">
                      {BLACKLIST_KEYS.map((key) => (
                        <label key={key} className="drug-checkbox-label">
                          <input
                            type="checkbox"
                            checked={editedBlacklists[key] || false}
                            onChange={(e) =>
                              setEditedBlacklists((prev) => ({
                                ...prev,
                                [key]: e.target.checked,
                              }))
                            }
                          />
                          {BLACKLIST_LABELS[key]}
                        </label>
                      ))}
                    </div>
                  </div>

                  <div
                    style={{
                      marginTop: '40px',
                      marginBottom: '12px',
                      color: '#103C6E',
                      fontSize: '16px',
                      fontWeight: 500,
                      lineHeight: '1.5',
                    }}
                    dir="rtl"
                  >
                    התאמת מינון לפי תפקוד כלייתי
                  </div>

                  <div className="formula-edit-row">
                    <span className="formula-edit-label">
                      המודיאליזה
                    </span>
                    <input
                      className="formula-edit-input"
                      value={editedDrug?.renalAdjustmentAdult?.hemodialysis || ''}
                      onChange={(e) =>
                        setEditedDrug((prev) => ({
                          ...prev,
                          renalAdjustmentAdult: {
                            ...EMPTY_RENAL_ADJUSTMENT_ADULT,
                            ...(prev?.renalAdjustmentAdult || {}),
                            hemodialysis: e.target.value,
                          },
                        }))
                      }
                      placeholder="e.g. 500 mg q24h"
                      dir="ltr"
                    />
                  </div>

                  <div className="formula-edit-row">
                    <span className="formula-edit-label">CAPD</span>
                    <input
                      className="formula-edit-input"
                      value={editedDrug?.renalAdjustmentAdult?.capd || ''}
                      onChange={(e) =>
                        setEditedDrug((prev) => ({
                          ...prev,
                          renalAdjustmentAdult: {
                            ...EMPTY_RENAL_ADJUSTMENT_ADULT,
                            ...(prev?.renalAdjustmentAdult || {}),
                            capd: e.target.value,
                          },
                        }))
                      }
                      placeholder="e.g. 500 mg q24h"
                      dir="ltr"
                    />
                  </div>

                  <div className="formula-edit-row">
                    <span className="formula-edit-label">CRRT</span>
                    <input
                      className="formula-edit-input"
                      value={editedDrug?.renalAdjustmentAdult?.crrt || ''}
                      onChange={(e) =>
                        setEditedDrug((prev) => ({
                          ...prev,
                          renalAdjustmentAdult: {
                            ...EMPTY_RENAL_ADJUSTMENT_ADULT,
                            ...(prev?.renalAdjustmentAdult || {}),
                            crrt: e.target.value,
                          },
                        }))
                      }
                      placeholder="e.g. 500 mg q24h"
                      dir="ltr"
                    />
                  </div>

                  {renderCctRanges({
                    ranges: editedDrug?.renalAdjustmentAdult?.cct,
                    onChange: updateEditedDrugCct,
                    onRemove: removeEditedDrugCctRange,
                    error: editedCctError,
                    onAdd: addEditedDrugCctRange,
                    onBlur: validateEditedCct,
                    idPrefix: 'edit-cct-range',
                  })}

                  <div className="formula-edit-actions">
                    <LoadingSubmitButton
                      onClick={saveEditing}
                      isLoading={isSaving}
                      text="שמירת שינויים"
                      loadingText="שומר..."
                      style={{
                        backgroundColor: '#103C6E',
                        borderRadius: '8px',
                        padding: '8px 30px',
                        border: 'none',
                        color: '#fff',
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                        fontWeight: 'bold',
                      }}
                    />
                    <Button
                      variant="outlined"
                      onClick={cancelEditing}
                      disabled={isSaving}
                      sx={{
                        borderColor: '#103C6E',
                        color: '#103C6E',
                        borderRadius: '8px',
                        padding: '8px 30px',
                        marginRight: '12px',
                      }}
                    >
                      בטל
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="formula-header">
                  <div className="formula-info">
                    <div className="formula-title">{drug.displayName}</div>
                    {blacklistLabels.length > 0 && (
                      <div className="drug-badges">
                        {blacklistLabels.map((label) => (
                          <span key={label} className="drug-badge blacklist-badge">
                            {label}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="formula-actions">
                    <div className="action-icon edit-icon" onClick={() => startEditing(index)}>
                      <EditIcon />
                    </div>

                    {deletingIndex === index ? (
                      <div className="action-icon style-loader">
                        <CircularProgress size={24} sx={{ color: '#103C6E' }} />
                      </div>
                    ) : (
                      <div
                        className="action-icon delete-icon"
                        onClick={() => handleDeleteClick(index)}
                      >
                        <DeleteIcon />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default DrugsTab;